/**
 * CompetizioneSelector — dropdown delle competizioni di una stagione.
 */

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useAuthStore } from "../../lib/auth-store";
import { type CompetizioneItem, type TipoCompetizione } from "../../hooks/useFase5";
import { tr } from "../../i18n";

const TIPO_LABEL: Record<TipoCompetizione, string> = {
  campionato: tr("Campionato"),
  champions_league: tr("Coppa"),
  supercoppa: tr("Supercoppa"),
};
// Andrea 2026-10-30: le stesse icone di Calendario e Classifica, niente
// piu' "(In corso)" / "(Da iniziare)" nelle voci.
export const TIPO_EMOJI: Record<TipoCompetizione, string> = {
  campionato: "📊",
  champions_league: "🏆",
  supercoppa: "🏆",
};

/** Valore speciale del select per "Tutte le competizioni" — usato quando
 * `includeAll=true`. Il chiamante deve gestire questo caso. */
export const COMP_ALL = "ALL";

interface CompetizioneSelectorProps {
  stagioneId: string | null;
  value: string;
  onChange: (id: string) => void;
  /** Filtra solo competizioni di un certo tipo. Default: tutte */
  tipo?: TipoCompetizione;
  /** Se true auto-seleziona la prima 'in_corso'. Default false */
  autoSelectActive?: boolean;
  /** Se true mostra anche l'opzione "Tutte le competizioni" (valore COMP_ALL). */
  includeAll?: boolean;
  label?: string;
  className?: string;
}

export function CompetizioneSelector({
  stagioneId,
  value,
  onChange,
  tipo,
  autoSelectActive = false,
  includeAll = false,
  label = tr("Competizione"),
  className = "",
}: CompetizioneSelectorProps) {
  const isAuth = !!useAuthStore((s) => s.accessToken);
  const { data: comps, isLoading } = useQuery({
    queryKey: ["stagione-competizioni-select", stagioneId],
    queryFn: async () =>
      (await api.get<CompetizioneItem[]>(`/stagioni/${stagioneId}/competizioni`)).data,
    enabled: isAuth && !!stagioneId,
  });

  const filtered = tipo ? (comps?.filter((c) => c.tipo === tipo) ?? []) : (comps ?? []);

  useEffect(() => {
    if (!autoSelectActive || value || !filtered.length) return;
    const attiva = filtered.find((c) => c.stato === "in_corso") ?? filtered[0];
    if (attiva) onChange(attiva.id);
  }, [filtered, value, autoSelectActive, onChange]);

  return (
    <div className={className}>
      {label && (
        <label className="block text-xs text-fg-muted uppercase tracking-wider mb-1">{label}</label>
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!stagioneId || isLoading || !filtered.length}
        className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
      >
        {!stagioneId && <option>{tr("— seleziona stagione prima —")}</option>}
        {stagioneId && isLoading && <option>{tr("Caricamento…")}</option>}
        {stagioneId && !isLoading && !filtered.length && (
          <option value="">{tr("— nessuna competizione —")}</option>
        )}
        {stagioneId && !isLoading && filtered.length > 0 && (
          <>
            <option value="">{tr("— seleziona —")}</option>
            {includeAll && <option value={COMP_ALL}>{tr("🌐 Tutte le competizioni")}</option>}
            {filtered.map((c) => (
              <option key={c.id} value={c.id}>
                {TIPO_EMOJI[c.tipo]} {TIPO_LABEL[c.tipo]} · {c.nome}
              </option>
            ))}
          </>
        )}
      </select>
    </div>
  );
}
